<template>
  <div ref="paginationRef" :class="{ 'hidden': hidden }" class="pagination-container">
    <el-pagination
      :background="background"
      v-model:current-page="currentPage"
      v-model:page-size="pageSize"
      :layout="layout"
      :page-sizes="pageSizes"
      :pager-count="pagerCount"
      :total="total"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />
  </div>
</template>

<script setup>
import { scrollTo } from '@/utils/scroll-to'

const route = useRoute()
const paginationRef = ref(null)

const props = defineProps({
  total: {
    required: true,
    type: Number
  },
  page: {
    type: Number,
    default: 1
  },
  limit: {
    type: Number,
    default: 20
  },
  pageSizes: {
    type: Array,
    default() {
      return [10, 20, 30, 50]
    }
  },
  // 移动端页码按钮的数量端默认值5
  pagerCount: {
    type: Number,
    default: document.body.clientWidth < 992 ? 5 : 7
  },
  layout: {
    type: String,
    default: 'total, sizes, prev, pager, next, jumper'
  },
  background: {
    type: Boolean,
    default: true
  },
  autoScroll: {
    type: Boolean,
    default: true
  },
  hidden: {
    type: Boolean,
    default: false
  },
  stateKey: {
    type: String,
    default: ''
  }
})

const emit = defineEmits();
const storageKey = ref('')
const restoringState = ref(false)
const isBusinessRoute = computed(() => {
  return Boolean(route.meta.businessView)
    || route.path.startsWith('/pc-operation/')
    || route.path.startsWith('/kefu/')
    || route.path.startsWith('/gly/')
    || route.path === '/direct-inquiry'
})

function resolveStorageKey() {
  if (props.stateKey) {
    return `business-pagination:${route.path}:${props.stateKey}`
  }
  const paginations = Array.from(document.querySelectorAll('.pagination-container'))
  const index = Math.max(0, paginations.indexOf(paginationRef.value))
  return `business-pagination:${route.path}:${index}`
}

function persistState(page, limit) {
  if (!isBusinessRoute.value || !storageKey.value || restoringState.value) return
  try {
    sessionStorage.setItem(storageKey.value, JSON.stringify({ page, limit }))
  } catch (_) { }
}

const currentPage = computed({
  get() {
    return props.page
  },
  set(val) {
    emit('update:page', val)
  }
})
const pageSize = computed({
  get() {
    return props.limit
  },
  set(val){
    emit('update:limit', val)
  }
})
function handleSizeChange(val) {
  const targetPage = currentPage.value * val > props.total ? 1 : currentPage.value
  if (targetPage !== currentPage.value) {
    currentPage.value = targetPage
  }
  persistState(targetPage, val)
  emit('pagination', { page: targetPage, limit: val })
  if (props.autoScroll) {
    scrollTo(0, 800)
  }
}
function handleCurrentChange(val) {
  persistState(val, pageSize.value)
  emit('pagination', { page: val, limit: pageSize.value })
  if (props.autoScroll) {
    scrollTo(0, 800)
  }
}

watch(() => [props.page, props.limit], ([page, limit]) => {
  persistState(page, limit)
})

onMounted(async () => {
  if (!isBusinessRoute.value) return
  await nextTick()
  storageKey.value = resolveStorageKey()
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey.value) || 'null')
    const savedPage = Number(saved?.page)
    const savedLimit = Number(saved?.limit)
    if (!Number.isInteger(savedPage) || savedPage < 1 || !Number.isInteger(savedLimit) || savedLimit < 1) return
    if (savedPage === props.page && savedLimit === props.limit) return

    restoringState.value = true
    emit('update:page', savedPage)
    emit('update:limit', savedLimit)
    await nextTick()
    emit('pagination', { page: savedPage, limit: savedLimit, restored: true })
  } catch (_) {
    sessionStorage.removeItem(storageKey.value)
  } finally {
    restoringState.value = false
  }
})

</script>

<style scoped>
.pagination-container {
  background: #fff;
  padding: 32px 16px;
}
.pagination-container.hidden {
  display: none;
}
</style>
